import { Form } from "@remix-run/react";
import {
  IconBrain,
  IconBrandJavascript,
  IconBrandNextjs,
  IconBrandReact,
  IconBrandSolidjs,
  IconBrandSvelte,
  IconBrandTypescript,
  IconBrandVue,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useRef } from "react";
import { Input } from "~/components/ui/input";
import { categories } from "~/utils/categories";
import { technologies } from "~/utils/technologies";

const technologyIcons = {
  javascript: IconBrandJavascript,
  typescript: IconBrandTypescript,
  react: IconBrandReact,
  vue: IconBrandVue,
  svelte: IconBrandSvelte,
  nextjs: IconBrandNextjs,
  solid: IconBrandSolidjs,
  ai: IconBrain,
};

type PageHeaderProps = {
  technology?: string;
  category?: string;
  searchTerm: string;
  hideSearch?: boolean;
};

export function PageHeader({
  technology,
  category,
  searchTerm,
  hideSearch = false,
}: PageHeaderProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const TechnologyIcon = technology
    ? technologyIcons[technology as keyof typeof technologyIcons]
    : null;

  const title = constructTitle(technology, category, searchTerm);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the key is "/" or "k" with the command/control key
      if (
        event.key === "/" ||
        ((event.metaKey || event.ctrlKey) && event.key === "k")
      ) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0 md:space-x-4 mt-6 lg:mt-12 lg:mb-8">
      <div className="flex items-center gap-x-4">
        {TechnologyIcon && (
          <TechnologyIcon size={32} className="text-primary max-md:hidden" />
        )}

        <h1 className="text-3xl font-bold">{title}</h1>
      </div>
      {!hideSearch && (
        <div className="flex items-center space-x-4 w-full md:w-auto">
          <Form action="/" method="get" className="flex-1 md:w-64 relative">
            <IconSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              size={18}
            />
            <Input
              type="search"
              name="q"
              placeholder="Search all projects..."
              className="w-full pl-10"
              defaultValue={searchTerm}
              ref={searchInputRef}
            />
          </Form>
        </div>
      )}
    </div>
  );
}

export function constructTitle(
  technology?: string,
  category?: string,
  searchTerm?: string
): string {
  let title = "Top";

  if (technology && technology !== "all") {
    const tech = technologies.find((t) => t.name === technology);
    title += ` ${
      tech ? tech.formattedName : capitalizeFirstLetter(technology)
    }`;
  }

  if (category && category !== "all") {
    const cat = categories.find((c) => c.name === category);
    title += ` ${cat ? cat.formattedName : capitalizeFirstLetter(category)}`;
  }

  if (searchTerm) {
    title += ` ${capitalizeFirstLetter(searchTerm)}`;
  }

  if (!searchTerm && (!category || category === "ai" || category === "all")) {
    title += " Projects";
  } else {
    title += " Libraries";
  }

  if (
    (!technology || technology === "all") &&
    (!category || category === "all") &&
    !searchTerm
  ) {
    return "Top Projects Across the Web";
  }

  return title;
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
